/**
 * Public offline cache for MyServiceHub discovery.
 *
 * This cache deliberately contains only public storefront/marketplace data.
 * It must never contain enquiries, customers, employees, subscriptions,
 * analytics, credentials, auth/session data, or admin data.
 */

export interface PublicBusinessSnapshot {
  id: string;
  slug?: string | null;
  business_name?: string | null;
  name?: string | null;
  country?: string | null;
  business_type?: string | null;
  verification_status?: string | null;
  badge_type?: string | null;
  trust_score?: number | null;
  whatsapp_number?: string | null;
  location?: string | null;
  primary_color?: string | null;
  secondary_color?: string | null;
  logo_url?: string | null;
  description?: string | null;
  is_verified?: boolean | null;
}

interface CacheEnvelope<T> {
  version: 1;
  cached_at: string;
  data: T;
}

const BUSINESS_PREFIX = 'msh:offline:business:';
const LISTINGS_PREFIX = 'msh:offline:listings:';
const MARKETPLACE_PREFIX = 'msh:offline:marketplace:';
const MAX_LISTINGS = 48;
const MAX_MARKETPLACE = 48;
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

function read<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const envelope = JSON.parse(raw) as CacheEnvelope<T>;
    if (!envelope || envelope.version !== 1 || !envelope.cached_at) return null;
    if (Date.now() - new Date(envelope.cached_at).getTime() > MAX_AGE_MS) return null;
    return envelope.data ?? null;
  } catch {
    return null;
  }
}

function write<T>(key: string, data: T): void {
  try {
    const envelope: CacheEnvelope<T> = {
      version: 1,
      cached_at: new Date().toISOString(),
      data
    };
    localStorage.setItem(key, JSON.stringify(envelope));
  } catch (error) {
    console.warn('[OfflineCache] Public cache write skipped:', error);
  }
}

export function toPublicBusinessSnapshot(business: any): PublicBusinessSnapshot | null {
  if (!business?.id) return null;
  return {
    id: String(business.id),
    slug: business.slug ?? null,
    business_name: business.business_name ?? null,
    name: business.name ?? null,
    country: business.country ?? null,
    business_type: business.business_type ?? null,
    verification_status: business.verification_status ?? null,
    badge_type: business.badge_type ?? null,
    trust_score: typeof business.trust_score === 'number' ? business.trust_score : null,
    whatsapp_number: business.whatsapp_number ?? null,
    location: business.location ?? null,
    primary_color: business.primary_color ?? null,
    secondary_color: business.secondary_color ?? null,
    logo_url: business.logo_url ?? null,
    description: business.description ?? null,
    is_verified: business.is_verified ?? null
  };
}

export function cachePublicBusiness(business: any): void {
  const snapshot = toPublicBusinessSnapshot(business);
  if (snapshot) write(BUSINESS_PREFIX + snapshot.id, snapshot);
  if (snapshot?.slug) write(BUSINESS_PREFIX + 'slug:' + snapshot.slug, snapshot);
}

export function getCachedBusiness(identifier?: string | null): PublicBusinessSnapshot | null {
  if (!identifier) return null;
  const direct = read<PublicBusinessSnapshot>(BUSINESS_PREFIX + identifier);
  if (direct) return direct;
  return read<PublicBusinessSnapshot>(BUSINESS_PREFIX + 'slug:' + identifier);
}

export function cachePublicBusinessListings(businessId: string, listings: any[]): void {
  if (!businessId) return;
  const publicListings = listings.slice(0, MAX_LISTINGS).map((listing) => ({
    id: listing.id,
    business_id: listing.business_id,
    title: listing.title,
    description: listing.description,
    price: listing.price,
    category: listing.category,
    type: listing.type,
    images: Array.isArray(listing.images) ? listing.images.slice(0, 8) : [],
    location: listing.location,
    currency: listing.currency,
    interaction_mode: listing.interaction_mode,
    is_affiliate: Boolean(listing.is_affiliate),
    affiliate_url: listing.affiliate_url || '',
    affiliate_button_text: listing.affiliate_button_text || '',
    pricing_tiers: listing.pricing_tiers || {},
    business_name: listing.business_name,
    business_type: listing.business_type,
    verification_status: listing.verification_status,
    badge_type: listing.badge_type,
    trust_score: listing.trust_score,
    whatsapp_number: listing.whatsapp_number,
    business_slug: listing.business_slug,
    stock_quantity: listing.stock_quantity,
    track_inventory: Boolean(listing.track_inventory),
    is_active: listing.is_active !== false
  }));
  write(LISTINGS_PREFIX + businessId, publicListings);
}

export function getCachedBusinessListings(businessId: string): any[] {
  if (!businessId) return [];
  return read<any[]>(LISTINGS_PREFIX + businessId) || [];
}

export function cachePublicMarketplace(country: string, listings: any[]): void {
  if (!country) return;
  const publicListings = listings.slice(0, MAX_MARKETPLACE).map((listing) => ({
    id: listing.id,
    business_id: listing.business_id,
    title: listing.title,
    description: listing.description,
    price: listing.price,
    category: listing.category,
    type: listing.type,
    images: Array.isArray(listing.images) ? listing.images.slice(0, 8) : [],
    location: listing.location,
    currency: listing.currency,
    interaction_mode: listing.interaction_mode,
    is_affiliate: Boolean(listing.is_affiliate),
    affiliate_url: listing.affiliate_url || '',
    affiliate_button_text: listing.affiliate_button_text || '',
    pricing_tiers: listing.pricing_tiers || {},
    business_name: listing.business_name,
    business_type: listing.business_type,
    verification_status: listing.verification_status,
    badge_type: listing.badge_type,
    trust_score: listing.trust_score,
    whatsapp_number: listing.whatsapp_number,
    business_slug: listing.business_slug,
    stock_quantity: listing.stock_quantity,
    track_inventory: Boolean(listing.track_inventory),
    is_active: listing.is_active !== false
  }));
  write(MARKETPLACE_PREFIX + country.toLowerCase(), publicListings);
}

export function getCachedMarketplace(country: string): any[] {
  if (!country) return [];
  return read<any[]>(MARKETPLACE_PREFIX + country.toLowerCase()) || [];
}

export function getPublicCacheAge(identifier: string, prefix = BUSINESS_PREFIX): string | null {
  try {
    const raw = localStorage.getItem(prefix + identifier);
    if (!raw) return null;
    const envelope = JSON.parse(raw) as CacheEnvelope<unknown>;
    return envelope.cached_at || null;
  } catch {
    return null;
  }
}
